// One visual language for every list-screen filter bar. The search box and the dropdowns are the
// same pill, the same height and the same type scale, so a toolbar reads as a single row of
// controls rather than a set of unrelated inputs. Before these tokens existed each screen styled
// its own bar: User Onboarding used a 44px pill with a green focus ring, Financial Support a 40px
// pill that never showed focus, Mobile Users an unstyled `size="small"` field and Country Locations
// the 48px form-field style meant for creation screens — four different toolbars for one job.
//
// Prefer the FilterSearchField / FilterSelect components in components/filterControls.jsx; reach
// for these tokens directly only for a control those two do not cover, so the whole bar stays
// aligned by construction.

// Filter controls are deliberately shorter than the 48px form fields in formStyles.js: a filter
// narrows a list, it does not collect data, and matching the form height makes a toolbar read as
// an editable form.
export const filterControlHeight = 44;

// Default width for a dropdown. Wide enough that "All Personas" or "All Providers" does not
// truncate, and every select on a bar shares it unless its longest option needs more.
export const filterSelectMinWidth = 170;

export const filterControlSx = {
  '& .MuiOutlinedInput-root': {
    height: filterControlHeight,
    borderRadius: '999px',
    backgroundColor: '#F7F7F7',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: 'transparent' },
    // The only state that draws a border. Keyboard users get no other cue that focus has landed
    // on a control with no resting outline, so this is an accessibility affordance, not decoration.
    '&.Mui-focused fieldset': { borderColor: '#4F8506' },
  },
  // Both selectors, so a Select and a TextField on the same bar sit on the same baseline with the
  // same type — styling only one is what made the old bars look ragged.
  '& .MuiSelect-select, & .MuiOutlinedInput-input': {
    py: '10px',
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    fontWeight: 500,
    color: '#33343D',
  },
};

export const filterMenuProps = {
  PaperProps: {
    sx: {
      mt: 1,
      maxHeight: 320,
      borderRadius: '12px',
      border: '1px solid #E4E6F1',
      boxShadow: '0px 8px 24px rgba(31, 46, 85, 0.08)',
      '& .MuiMenuItem-root': {
        minHeight: filterControlHeight,
        fontSize: 16,
        lineHeight: '24px',
        letterSpacing: '0.5px',
        fontWeight: 400,
        color: '#1C1B1F',
      },
    },
  },
};

// The row the controls sit in. Wraps rather than scrolls, so a narrow window stacks the controls
// instead of hiding the ones on the right.
export const filterBarSx = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1.5,
  mb: 2,
};

// Borderless on purpose: it is an escape hatch that appears only once a filter is active, and a
// third bordered pill next to the real controls reads as another filter.
export const clearFiltersButtonSx = {
  height: filterControlHeight,
  px: 2,
  borderRadius: '999px',
  textTransform: 'none',
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.1px',
  fontWeight: 500,
  color: '#4F8506',
};
